// Pure view-model for a v3 use case page (stories 31-43): turns the engine's result and the catalog
// into the plain shapes the page renders. No React, no store, no storage, so tests build inputs
// directly. The page component only lays these out.

import type {
  LeadingMetric,
  Measurement,
  QuestionRole,
  RecordLevel,
  SizedUseCase,
  UseCaseQuestion,
} from '../../data/use-case-catalog';
import { bmEff } from '../../engine/benchmarks-v3';
import { attributedLabelFor } from './attributed-label';
import { money, moneyBig, num } from '../../engine/format';
import { visibleBenchmarkRows, type BmRow } from '../../engine/migrate-v3';
import {
  enabledMethods,
  resolveUseCaseAnswers,
  type FormulaSection,
  type FormulaStep,
  type MetricValue,
  type StepUnit,
  type UseCaseAnswers,
  type UseCaseResult,
  type UseCaseSelection,
} from '../../engine/use-case-engine';

/* ------------------------------------------------------------------------ header */

export const CONFIDENCE_LABEL: Record<SizedUseCase['confidence'], string> = {
  Benchmarked: 'Benchmarked',
  Partial: 'Derived estimate',
};

export const CONFIDENCE_HELP: Record<SizedUseCase['confidence'], string> = {
  Benchmarked: 'The lift Clay is claiming here is backed by results from Clay customers.',
  Partial: 'The lift is derived from your own funnel, not from a published customer result.',
};

export const LEVEL_LABEL: Record<RecordLevel, string> = { accounts: 'Accounts', contacts: 'Contacts' };

/** The record level control's three choices, as the levels each turns on. */
export const LEVEL_CHOICES: { id: 'accounts' | 'contacts' | 'both'; label: string; levels: RecordLevel[] }[] = [
  { id: 'accounts', label: 'Accounts', levels: ['accounts'] },
  { id: 'contacts', label: 'Contacts', levels: ['contacts'] },
  { id: 'both', label: 'Both', levels: ['accounts', 'contacts'] },
];

/**
 * Which first-visit entry choice a use case page asks before its inputs, if any. Derived, not stored:
 * the level / method pick is only written when the rep clicks one, so "unset" means "never chosen".
 * Any answer or factor override also counts as chosen, so older payloads with inputs open straight on
 * the input page.
 */
export function needsEntryChoice(uc: SizedUseCase, sel: UseCaseSelection): 'levels' | 'methods' | null {
  const touched =
    Object.keys(sel.answers ?? {}).length > 0 ||
    (sel.realization ?? '') !== '' ||
    (sel.attribution ?? '') !== '';
  if (touched) return null;
  if (uc.recordLevels.length > 1 && !sel.recordLevels?.length) return 'levels';
  if (uc.methods.length > 1 && !sel.methods?.length) return 'methods';
  return null;
}

/** A calculation method's letter in the picker and headings: the first is A, the second B... */
export function methodLetter(uc: SizedUseCase, methodId: string): string {
  return String.fromCharCode(65 + Math.max(0, uc.methods.findIndex((m) => m.id === methodId)));
}

export function levelChoiceOf(levels: readonly RecordLevel[]): 'accounts' | 'contacts' | 'both' {
  if (levels.length > 1) return 'both';
  return levels[0] === 'contacts' ? 'contacts' : 'accounts';
}

/* ----------------------------------------------------------------------- formatting */

const round1 = (v: number) => Math.round(v * 10) / 10;

/** A formula card's value: money compact, rates as percents, counts without false precision. */
export function formatStepValue(v: number, unit: StepUnit): string {
  const n = Number.isFinite(v) ? v : 0;
  switch (unit) {
    case 'money':
      return Math.abs(n) < 1000 && !Number.isInteger(n) ? `$${n.toFixed(2)}` : money(n);
    case 'pct':
      return `${Math.round(n * 100) / 100}%`;
    case 'hours':
      return `${num(n)} hrs`;
    case 'count':
      return Math.abs(n) < 100 && !Number.isInteger(n) ? String(round1(n)) : num(n);
  }
}

/**
 * A difference is shown with its sign ("+2.5%", "+1,200"): the change Clay adds, never a bare number.
 * Display only; zero stays "0" and a negative keeps its own minus.
 */
export function signed(display: string, value: number): string {
  return value > 0 && !display.startsWith('+') ? `+${display}` : display;
}

/** Computed cards that are themselves a difference or an addition Clay makes. */
const DIFFERENCE_LABEL = /^(Net-new|Added|Additional)\b/;

/** An input's placeholder: the default, formatted for its kind. */
export function formatDefault(kind: UseCaseQuestion['kind'], v: number): string {
  if (kind === 'money' || kind === 'rate') return `$${Number(v).toLocaleString('en-US')}`;
  if (kind === 'pct') return `${v}%`;
  if (kind === 'hrs') return `${v} hrs`;
  if (kind === 'min') return `${v} min`;
  return Number(v).toLocaleString('en-US');
}

/* ------------------------------------------------------------------------ questions */

export const ROLE_ORDER: QuestionRole[] = ['scaling', 'current', 'with-clay'];

export const ROLE_COPY: Record<QuestionRole, { title: string; blurb: string }> = {
  scaling: { title: 'Scaling', blurb: 'The size of your motion and how it converts.' },
  current: { title: 'Current state', blurb: 'Where you are today, before Clay.' },
  'with-clay': { title: 'With Clay', blurb: 'What Clay is expected to change. This is Clay’s claim.' },
};

export interface QuestionVm {
  key: string;
  label: string;
  kind: UseCaseQuestion['kind'];
  /** The default this input falls back to (PLG ACV: the deal's ACV). */
  def: number;
  defText: string;
  /** Still on the Clay default (no answer stored). */
  assumed: boolean;
  /** Set only when more than one record level is on, so the SE can tell the rows apart. */
  level: RecordLevel | null;
  /** Set only when more than one calculation method is on, so the SE can tell the rows apart. */
  method: string | null;
}

export interface QuestionGroupVm {
  role: QuestionRole;
  title: string;
  blurb: string;
  questions: QuestionVm[];
}

export function effectiveDefault(q: UseCaseQuestion, dealAcv: number): number {
  return q.defaultFromDealAcv ? dealAcv : q.def;
}

/** The questions asked at the enabled record levels, grouped Scaling / Current state / With Clay. */
export function questionGroups(
  uc: SizedUseCase,
  levels: readonly RecordLevel[],
  answers: UseCaseAnswers | null | undefined,
  dealAcv: number,
  methods?: readonly string[] | null,
): QuestionGroupVm[] {
  const saved = answers ?? {};
  const on = enabledMethods(uc, methods);
  const asked = uc.questions.filter(
    (q) => (!q.recordLevel || levels.includes(q.recordLevel)) && (!q.method || on.includes(q.method)),
  );
  return ROLE_ORDER.map((role) => ({
    role,
    ...ROLE_COPY[role],
    questions: asked
      .filter((q) => q.role === role)
      .map((q) => {
        const def = effectiveDefault(q, dealAcv);
        return {
          key: q.key,
          label: q.label,
          kind: q.kind,
          def,
          defText: formatDefault(q.kind, def),
          assumed: saved[q.key] === undefined,
          level: levels.length > 1 ? (q.recordLevel ?? null) : null,
          method: on.length > 1 ? (q.method ?? null) : null,
        };
      }),
  })).filter((g) => g.questions.length > 0);
}

/* ------------------------------------------------------------------------ formula */

export type CardKind = 'input' | 'step' | 'subtotal' | 'total';

export interface FormulaCardVm {
  label: string;
  description: string;
  display: string;
  /** Joins this card to the previous one; null on the first. */
  operator: FormulaStep['operator'];
  kind: CardKind;
}

/** Formula steps -> cards. '=' cards that carry a sub-total key are sub-totals; the last card is the total. */
export function formulaCards(steps: readonly FormulaStep[]): FormulaCardVm[] {
  return steps.map((s, i) => ({
    label: s.label,
    description: s.description,
    display: formatStepValue(s.value, s.unit),
    operator: s.operator,
    kind: i === steps.length - 1 ? 'total' : s.subTotal ? 'subtotal' : i === 0 ? 'input' : 'step',
  }));
}

export interface SectionVm {
  level: RecordLevel | null;
  title: string | null;
  cards: FormulaCardVm[];
  /** Inbound: the today and with-Clay chains shown above the difference. */
  columns: { today: FormulaCardVm[]; withClay: FormulaCardVm[] } | null;
  valueText: string;
}

export function sectionVms(sections: readonly FormulaSection[], showTitles: boolean): SectionVm[] {
  return sections.map((s) => ({
    level: s.level,
    title: showTitles && s.level ? LEVEL_LABEL[s.level] : null,
    cards: formulaCards(s.steps),
    columns: s.columns
      ? { today: formulaCards(s.columns.today), withClay: formulaCards(s.columns.withClay) }
      : null,
    valueText: moneyBig(s.value),
  }));
}

/* --------------------------------------------------------------------------- hero */

export const MEASUREMENT_LABEL: Record<Measurement, string> = {
  'telemetry-capable': 'Telemetry-capable',
  'customer-reported': 'Customer-reported',
};

export interface LeadingVm {
  key: string;
  name: string;
  levelLabel: string | null;
  measurement: string;
  today: string;
  /** null when the metric has no today / with-Clay split. */
  withClay: string | null;
  unitNote: string;
  /** The today value is a default (never flagged for fixed-zero baselines). */
  assumed: boolean;
}

export interface HeroVm {
  leading: LeadingVm[];
  laggingLabel: string;
  laggingDisplay: string;
  /** Pipeline created, shown small under the revenue. Null outside the pipeline path. */
  pipelineNote: string | null;
}

/** A leading metric's value for its unit: `%` units as percents, everything else as a plain number. */
export function formatMetric(v: number, unit: string): string {
  return unit.startsWith('%') ? `${Math.round(v * 100) / 100}%` : num(v);
}

const unitNoteOf = (unit: string) => (unit.startsWith('%') ? unit.replace(/^%\s*/, '') : unit);

export function heroVm(uc: SizedUseCase, result: UseCaseResult): HeroVm {
  const byKey = new Map<string, LeadingMetric>(uc.leadingMetrics.map((m) => [m.key, m]));
  const leading = result.metrics.map((m: MetricValue) => ({
    key: m.key,
    name: m.name,
    levelLabel: m.recordLevel && result.recordLevels.length > 1 ? LEVEL_LABEL[m.recordLevel] : null,
    measurement: MEASUREMENT_LABEL[m.measurement],
    today: formatMetric(m.today, m.unit),
    withClay: m.withClay === null ? null : formatMetric(m.withClay, m.unit),
    unitNote: unitNoteOf(m.unit),
    assumed: m.assumed && byKey.get(m.key)?.today.kind !== 'constant',
  }));
  return {
    leading,
    laggingLabel: uc.outcome === 'cost' ? 'Cost saved per year' : 'Revenue per year',
    laggingDisplay: moneyBig(result.value),
    pipelineNote: result.pipeline > 0 ? `${money(result.pipeline)} pipeline created` : null,
  };
}

/* ---------------------------------------------------------------- realization / attribution */

export interface FactorSummary {
  program: number;
  attributed: number;
  programText: string;
  attributedText: string;
}

/** Gross value -> program value (after realization) -> Clay-attributed value (after attribution). */
export function factorSummary(gross: number, realizationPct: number, attributionPct: number): FactorSummary {
  const program = (gross * realizationPct) / 100;
  const attributed = (program * attributionPct) / 100;
  return { program, attributed, programText: moneyBig(program), attributedText: moneyBig(attributed) };
}

/** One figure on the Summary strip's tile. */
export interface SummaryFigureVm {
  label: string;
  text: string;
}

/**
 * The Summary strip's right-hand tile: program value (lime) over Clay-attributed (slushie), both at
 * full emphasis. The attributed label follows the use case's outcome.
 */
export interface SummaryTileVm {
  program: SummaryFigureVm;
  attributed: SummaryFigureVm;
}

export function summaryTileVm(uc: SizedUseCase, gross: number, realizationPct: number, attributionPct: number): SummaryTileVm {
  const f = factorSummary(gross, realizationPct, attributionPct);
  return {
    program: { label: 'Program value', text: f.programText },
    attributed: { label: attributedLabelFor(uc.outcome), text: f.attributedText },
  };
}

/* -------------------------------------------------------------------------- benchmarks */

export interface BenchmarkRowVm {
  id: string;
  customer: string;
  headline: string;
  detail: string;
  notes: string;
}

/** The first few visible benchmark rows for a use case; rows with a result to quote come first. */
export function topBenchmarkRows(rows: readonly BmRow[], useCaseId: string, limit = 3): BenchmarkRowVm[] {
  const vms = visibleBenchmarkRows([...rows])
    .filter((r) => r.metricId === useCaseId)
    .map((r) => {
      const headline = bmEff(r) || r.post.trim() || r.pre.trim();
      const detail = r.pre.trim() && r.post.trim() ? `${r.pre.trim()} → ${r.post.trim()}` : '';
      return { id: r.id, customer: r.customer, headline, detail, notes: r.notes };
    });
  const quotable = vms.filter((r) => r.headline);
  return [...quotable, ...vms.filter((r) => !r.headline)].slice(0, limit);
}

/* ------------------------------------------------------------------ editable formula chain */
//
// The use case page's "How the number is built" is also its editing surface: engine steps become
// typed cards, each carrying the catalog question key(s) it edits. The engine's step list stays
// untouched (golden-v3.json pins it), so the binding lives here, keyed by use case and step label.
// `chainEditableKeys` + a unit test guarantee every catalog question is editable from exactly one card.

export type DealField = 'acv' | 'winRate' | 'currentAnnualPipeline';
export type ChainOperator = '×' | '−' | '+' | '=';
export type ChainCardType = 'input' | 'delta' | 'deal' | 'subtotal' | 'result' | 'factor' | 'attributed';

export interface ChainCardVm {
  kind: 'card';
  id: string;
  type: ChainCardType;
  label: string;
  description: string;
  /** Computed display (the value a read-only reader sees; for an input, the value in force). */
  display: string;
  /** Joins this card to the one before it (or, first in a row, the previous row). */
  operator: ChainOperator | null;
  /** Catalog question keys edited here: one for an input, [today, withClay] for a delta. Empty otherwise. */
  keys: string[];
  /** Input / delta only: the editor's field kind, for the unit affix. */
  questionKind: UseCaseQuestion['kind'] | null;
  /** Deal card only. */
  dealField: DealField | null;
  /** Factor card only. */
  factor: 'realization' | 'attribution' | null;
  /** The headline totals (pipeline, revenue) get double emphasis on top of the computed-card style. */
  strong: boolean;
  /**
   * Win-rate increase only: the card edits ONE relative-lift question; today's rate comes from the
   * deal (read-only) and the with-Clay rate is today × (1 + lift).
   */
  winRateLift: boolean;
  /**
   * Inbound's lead-to-opportunity uplift: one card editing the four questions the uplift is built
   * from (match rate today and with Clay, conversion today, speed-to-lead lift).
   */
  compound: boolean;
  /** Grid columns the card spans (the compound card is double width). */
  span: 1 | 2;
  /**
   * A fixed constant folded into this computed card ("× 12 months", "÷ 2,080 hours"): it takes no
   * grid column, so it is shown as a small chip under the card's label.
   */
  caption: string | null;
}

/** Every chain item is a card; constants live on the card they feed (`caption`). */
export type ChainItemVm = ChainCardVm;

export interface ChainRowVm {
  id: string;
  /** Section heading, set on the first row of a record level's chain when both levels are on. */
  heading: string | null;
  items: ChainItemVm[];
}

type Binding =
  | { input: string; label?: string; chipAfter?: string }
  | { delta: [today: string, clay: string] }
  /** One relative-lift question, shown as a "today → with Clay" win-rate difference. */
  | { winRateLift: string }
  /** start / minus / eq of a "with Clay − today" count pair collapse into one delta card. */
  | { pair: [today: string, clay: string] }
  | { deal: DealField }
  | { chip: string }
  | { skip: true };

const DEAL_TAIL: Record<string, Binding> = {
  'Average deal size': { deal: 'acv' },
  'Win rate': { deal: 'winRate' },
};

const BINDINGS: Record<string, Record<string, Binding>> = {
  'uc-crm-enrichment': {
    ...DEAL_TAIL,
    'Account records': { input: 'accounts_records' },
    'Change in fill rate': { delta: ['accounts_fill_current', 'accounts_fill_clay'] },
    'Records-to-opportunity rate': { input: 'accounts_record_to_opp' },
    Contacts: { input: 'contacts_records' },
    'Change in contactable rate': { delta: ['contacts_reachable_current', 'contacts_reachable_clay'] },
    'Share worked': { input: 'contacts_share_worked' },
    'Contact-to-meeting rate': { input: 'contacts_to_meeting' },
    'Meeting-to-opportunity rate': { input: 'contacts_meeting_to_opp' },
  },
  'uc-account-lead-scoring': {
    'Current annual pipeline': { deal: 'currentAnnualPipeline' },
    'Change in scoring coverage': { delta: ['coverage_current', 'coverage_clay'] },
    'Win-rate increase': { winRateLift: 'winrate_lift' },
  },
  'uc-tam-sourcing': {
    ...DEAL_TAIL,
    'Accounts sourced per year with Clay': { pair: ['accounts_year_today', 'accounts_year_clay'] },
    'Account-to-opportunity rate': { input: 'accounts_to_opp' },
    'Contacts sourced per year with Clay': { pair: ['contacts_year_today', 'contacts_year_clay'] },
    'Contact-to-meeting rate': { input: 'contacts_to_meeting' },
    'Meeting-to-opportunity rate': { input: 'contacts_meeting_to_opp' },
  },
  'uc-inbound-speed-to-lead': { ...DEAL_TAIL },
  'uc-automated-outbound': {
    ...DEAL_TAIL,
    'Outreach sent per year': { input: 'outreach_year' },
    'Meeting rate': { input: 'meeting_rate' },
    'Meeting-to-opportunity rate': { input: 'meeting_to_opp' },
  },
  'uc-performance-marketing': {
    'Annual ad spend': { input: 'ad_spend_year' },
    'Change in audience match rate': { delta: ['match_current', 'match_clay'] },
    'Recoverable share': { input: 'recoverable_pct' },
  },
  'uc-rep-prospecting': {
    ...DEAL_TAIL,
    'Prospects per rep per week with Clay': { pair: ['prospects_week_today', 'prospects_week_clay'] },
    Reps: { input: 'reps' },
    'Working weeks': { chip: '× 48 weeks' },
    'Meeting rate': { input: 'meeting_rate' },
    'Meeting-to-opportunity rate': { input: 'meeting_to_opp' },
  },
  'uc-signal-based-outbound': {
    ...DEAL_TAIL,
    'Signal-triggered contacts per month': { input: 'signal_contacts_month' },
    Months: { chip: '× 12 months' },
    'Change in meeting rate': { delta: ['meeting_rate_today', 'meeting_rate_clay'] },
    'Meeting-to-opportunity rate': { input: 'meeting_to_opp' },
  },
  'uc-plg-expansion': {
    'Win rate': { deal: 'winRate' },
    'Signups per month': { input: 'signups_month' },
    Months: { chip: '× 12 months' },
    'ICP fit': { input: 'icp_fit' },
    'Change in conversion': { delta: ['conversion_today', 'conversion_clay'] },
    'PLG average deal size': { input: 'plg_acv' },
  },
  'uc-abm': {
    'Average deal size': { deal: 'acv' },
    'Accounts enrolled': { input: 'accounts_enrolled' },
    'With an open opportunity': { input: 'open_opp_pct' },
    'Win-rate increase': { winRateLift: 'winrate_uplift' },
  },
  'uc-tool-consolidation': { 'Displaced tool contracts': { skip: true } },
  'uc-gtm-automation': {
    'Team hours per week': { input: 'hours_week' },
    'Automation share': { input: 'automation_pct' },
    Weeks: { chip: '× 52 weeks' },
    'Flat hours saved per week': { input: 'hours_saved_week' },
    'Minutes saved per record': { input: 'minutes_per_record' },
    'Records worked per week': { input: 'records_week' },
    'Hours conversion': { chip: '÷ 60 min × 52 weeks' },
    'Cost per hour': { input: 'annual_cost', label: 'Fully-loaded cost per year', chipAfter: '÷ 2,080 hours' },
  },
};

export interface ChainInput {
  uc: SizedUseCase;
  result: UseCaseResult;
  levels: readonly RecordLevel[];
  /** Stored answers (to flag nothing here; the card reads Assumed from the editor). */
  answers: UseCaseAnswers | null | undefined;
  dealAcv: number;
  realizationPct: number;
  attributionPct: number;
}

const fmtValue = (v: number, kind: UseCaseQuestion['kind']): string => {
  const n = Math.round(v * 100) / 100;
  if (kind === 'money' || kind === 'rate') return money(n);
  if (kind === 'pct') return `${n}%`;
  if (kind === 'hrs') return `${n} hrs`;
  if (kind === 'min') return `${n} min`;
  return num(n);
};

const DEAL_LABEL: Record<DealField, string> = {
  acv: 'Average deal size',
  winRate: 'Win rate',
  currentAnnualPipeline: 'Current annual pipeline',
};

function card(p: Partial<ChainCardVm> & Pick<ChainCardVm, 'id' | 'type' | 'label' | 'display'>): ChainCardVm {
  return {
    kind: 'card',
    description: '',
    operator: null,
    keys: [],
    questionKind: null,
    dealField: null,
    factor: null,
    strong: false,
    winRateLift: false,
    compound: false,
    span: 1,
    caption: null,
    ...p,
  };
}

/** Revenue (a result) and pipeline (the total multiplied by the win rate) carry double emphasis. */
function markHeadlines(items: ChainItemVm[]): ChainItemVm[] {
  return items.map((it, i) => {
    const next = items[i + 1];
    const pipeline = it.type === 'subtotal' && next && (next.dealField === 'winRate' || next.winRateLift);
    return it.type === 'result' || pipeline ? { ...it, strong: true } : it;
  });
}

/** One section's steps -> items, using the use case's bindings. `skip` leaves the leading step out. */
function sectionItems(
  input: ChainInput,
  steps: readonly FormulaStep[],
  resolved: Record<string, number>,
  idPrefix: string,
  finalType: 'result' | 'subtotal',
  finalLabel?: string,
  /** The run starts the chain, so its first card has no leading operator (false for inbound's tail). */
  startsChain = true,
): ChainItemVm[] {
  const { uc } = input;
  const binds = BINDINGS[uc.id] ?? {};
  const qkind = (key: string) => uc.questions.find((q) => q.key === key)?.kind ?? 'count';
  const items: ChainItemVm[] = [];
  // A constant ("× 12 months", "÷ 2,080 hours") attaches to the next card pushed.
  let pendingCaption: string | null = null;
  const push = (c: ChainCardVm) => {
    items.push(pendingCaption ? { ...c, caption: pendingCaption } : c);
    pendingCaption = null;
  };
  for (let i = 0; i < steps.length; i++) {
    const s = steps[i];
    const id = `${idPrefix}-${i}`;
    const b = binds[s.label];
    const last = i === steps.length - 1;
    const operator = items.length === 0 && startsChain ? null : s.operator;
    if (b && 'skip' in b) continue;
    if (b && 'chip' in b) {
      pendingCaption = b.chip;
      continue;
    }
    if (b && 'pair' in b) {
      // start (with Clay), minus (today), eq (difference): one editable delta card.
      const eq = steps[i + 2];
      i += 2;
      push(
        card({
          id,
          type: 'delta',
          label: eq.label,
          description: `Today → with Clay. ${eq.description}`,
          display: signed(formatStepValue(eq.value, eq.unit), eq.value),
          operator,
          keys: [...b.pair],
          questionKind: qkind(b.pair[0]),
        }),
      );
      continue;
    }
    if (b && 'delta' in b) {
      push(
        card({
          id,
          type: 'delta',
          label: s.label,
          description: s.description,
          display: signed(formatStepValue(s.value, s.unit), s.value),
          operator,
          keys: [...b.delta],
          questionKind: qkind(b.delta[0]),
        }),
      );
      continue;
    }
    if (b && 'winRateLift' in b) {
      push(
        card({
          id,
          type: 'delta',
          label: s.label,
          description: s.description,
          display: signed(formatStepValue(s.value, s.unit), s.value),
          operator,
          keys: [b.winRateLift],
          questionKind: 'pct',
          winRateLift: true,
        }),
      );
      continue;
    }
    if (b && 'input' in b) {
      const k = qkind(b.input);
      push(
        card({
          id,
          type: 'input',
          label: b.label ?? s.label,
          description: s.description,
          display: fmtValue(resolved[b.input] ?? 0, k),
          operator,
          keys: [b.input],
          questionKind: k,
        }),
      );
      if (b.chipAfter) pendingCaption = b.chipAfter;
      continue;
    }
    if (b && 'deal' in b) {
      push(
        card({
          id,
          type: 'deal',
          label: DEAL_LABEL[b.deal],
          description: s.description,
          display: formatStepValue(s.value, s.unit),
          operator,
          dealField: b.deal,
        }),
      );
      continue;
    }
    // Unbound: a computed card. '=' cards are sub-totals; the section's last card is its result.
    push(
      card({
        id,
        type: last ? finalType : 'subtotal',
        label: last && finalLabel ? finalLabel : s.label,
        description: s.description,
        display: !last && DIFFERENCE_LABEL.test(s.label) ? signed(formatStepValue(s.value, s.unit), s.value) : formatStepValue(s.value, s.unit),
        operator: operator ?? null,
      }),
    );
  }
  return markHeadlines(items);
}

/** Most cards a chain row holds: the grid has four columns. */
export const MAX_ROW_CARDS = 4;

/**
 * Use cases (and record level, "<id>/<level>") whose "=" segments are packed several to a row.
 * Packing is greedy: consecutive segments share a row while the row stays within four cards, so a
 * funnel reads `Meeting rate × = Meetings × Meeting-to-opportunity rate = Opportunities` on one
 * line and the deal tail reads `Average deal size = Pipeline × Win rate = Revenue`. Every other use
 * case keeps one segment per row (unchanged unless asked).
 */
const PACKED_ROWS = new Set([
  'uc-crm-enrichment/contacts',
  'uc-automated-outbound/',
  'uc-rep-prospecting/',
  'uc-signal-based-outbound/',
  'uc-plg-expansion/',
]);

/** Split cards into "=" segments (each ends at a card whose operator is "="). */
function segments(items: ChainItemVm[]): ChainItemVm[][] {
  const groups: ChainItemVm[][] = [];
  let cur: ChainItemVm[] = [];
  for (const it of items) {
    cur.push(it);
    if (it.operator === '=') {
      groups.push(cur);
      cur = [];
    }
  }
  if (cur.length) groups.push(cur);
  return groups;
}

/**
 * The deal tail: "x Average deal size = Pipeline" and "x Win rate (or win-rate increase) = Revenue"
 * always get a row of their own (operator + deal card + its "=" result), in every use case.
 */
export function isDealSegment(seg: readonly ChainItemVm[]): boolean {
  const first = seg[0];
  if (!first || seg.length !== 2) return false;
  return first.dealField === 'acv' || first.dealField === 'winRate' || first.winRateLift || /average deal size/i.test(first.label);
}

/**
 * Greedy packing: merge consecutive segments while the merged row holds at most `max` cards.
 * A segment for which `solo` is true never merges with its neighbours.
 */
export function packSegments<T>(groups: T[][], max: number = MAX_ROW_CARDS, solo: (g: T[]) => boolean = () => false): T[][] {
  const out: T[][] = [];
  let prevSolo = true;
  for (const g of groups) {
    const last = out.at(-1);
    const isSolo = solo(g);
    if (last && !prevSolo && !isSolo && last.length + g.length <= max) last.push(...g);
    else out.push([...g]);
    prevSolo = isSolo;
  }
  return out;
}

/**
 * Columns a chain's grid has: the card count of its widest row (1..4). Every card in the chain is
 * the same width, and shorter rows stay right-aligned so totals share the rightmost column.
 */
/** Grid columns a row fills: one per card, two for a double-width card. */
export function rowSpan(row: ChainRowVm): number {
  return row.items.reduce((n, c) => n + c.span, 0);
}

export function chainColumns(rows: readonly ChainRowVm[]): number {
  const widest = Math.max(1, ...rows.map(rowSpan));
  return Math.min(MAX_ROW_CARDS, widest);
}

/** Each card's 1-based start column in a right-aligned row, accounting for double-width cards. */
export function rowColumns(row: ChainRowVm, columns: number = MAX_ROW_CARDS): number[] {
  let col = Math.max(1, columns - rowSpan(row) + 1);
  return row.items.map((c) => {
    const at = col;
    col += c.span;
    return at;
  });
}

/** The 1-based grid column of card `index` in a row of `count` cards in a `columns`-column chain. */
export function cardColumn(count: number, index: number, columns: number = MAX_ROW_CARDS): number {
  return Math.max(1, columns - count + index + 1);
}

function toRows(items: ChainItemVm[], idPrefix: string, heading: string | null, pack: boolean): ChainRowVm[] {
  const segs = segments(items);
  const groups = pack ? packSegments(segs, MAX_ROW_CARDS, isDealSegment) : segs;
  return groups.map((g, i) => ({ id: `${idPrefix}-r${i}`, heading: i === 0 ? heading : null, items: g }));
}

/**
 * Inbound's chain: `Inbound leads × Qualified = Qualified leads × Lead-to-opportunity uplift =
 * Additional opportunities`. The uplift is the engine's per-qualified-lead rate with Clay minus
 * today, in percentage points, so qualified leads × uplift is exactly its "with Clay − today":
 *   today     = match rate today × conversion
 *   with Clay = match rate with Clay × conversion × (1 + speed-to-lead lift)
 * The double-width uplift card edits all four of those questions.
 */
function inboundRows(section: FormulaSection, resolved: Record<string, number>): ChainRowVm[] {
  const leads = resolved.leads_year ?? 0;
  const qualified = resolved.qualified_pct ?? 0;
  const matchToday = resolved.match_current ?? 0;
  const matchClay = resolved.match_clay ?? 0;
  const conversion = resolved.conversion_current ?? 0;
  const lift = resolved.speed_lift ?? 0;
  const rateToday = (matchToday * conversion) / 100;
  const rateClay = (matchClay * conversion * (100 + lift)) / 10_000;
  const uplift = rateClay - rateToday;
  const pct = (v: number) => formatStepValue(v, 'pct');
  const added = section.steps[2];
  return [
    {
      id: 'inb-r0',
      heading: null,
      items: [
        card({ id: 'inb-0', type: 'input', label: 'Inbound leads per year', description: 'All inbound leads', display: fmtValue(leads, 'count'), keys: ['leads_year'], questionKind: 'count' }),
        card({ id: 'inb-1', type: 'input', label: 'Qualified', description: 'Leads that are qualified', display: fmtValue(qualified, 'pct'), operator: '×', keys: ['qualified_pct'], questionKind: 'pct' }),
        card({ id: 'inb-2', type: 'subtotal', label: 'Qualified leads', description: 'Leads × qualified share', display: formatStepValue((leads * qualified) / 100, 'count'), operator: '=' }),
      ],
    },
    {
      id: 'inb-r1',
      heading: null,
      items: [
        card({
          id: 'inb-3',
          type: 'delta',
          label: 'Lead-to-opportunity uplift',
          description: `${pct(rateClay)} with Clay − ${pct(rateToday)} today. The uplift comes from better contact data matching and faster response to every lead.`,
          display: signed(pct(uplift), uplift),
          operator: '×',
          keys: ['match_current', 'match_clay', 'conversion_current', 'speed_lift'],
          questionKind: 'pct',
          compound: true,
          span: 2,
        }),
        card({ id: 'inb-4', type: 'subtotal', label: added?.label ?? 'Additional opportunities', description: 'Qualified leads × uplift', display: signed(formatStepValue(added?.value ?? 0, 'count'), added?.value ?? 0), operator: '=' }),
      ],
    },
  ];
}


/**
 * GTM automation's chain, one block per selected calculation method (A, B, C order), each ending in
 * its "hours saved per year" card and packed to as few rows as fit (method A's hours per week and
 * per year share one row). With two or more methods a sum row adds the yearly cards into the total
 * hours; the chain then closes with its own "× cost per hour = Labor cost saved" row.
 */
function gtmRows(input: ChainInput, section: FormulaSection, resolved: Record<string, number>): ChainRowVm[] {
  const { uc, result } = input;
  const methods = result.methods ?? [];
  const multi = methods.length > 1;
  const steps = section.steps;
  const tailAt = steps.findIndex((s) => s.label === 'Cost per hour');
  const head = steps.slice(0, tailAt);
  const totalStep = multi ? head.pop() : undefined;
  const blocks: FormulaStep[][] = [];
  let cur: FormulaStep[] = [];
  for (const st of head) {
    cur.push(st);
    if (st.subTotal?.startsWith('hours_year')) {
      blocks.push(cur);
      cur = [];
    }
  }
  const rows: ChainRowVm[] = [];
  blocks.forEach((block, i) => {
    const id = methods[i] ?? `m${i}`;
    const heading = multi ? (uc.methods.find((m) => m.id === id)?.heading ?? null) : null;
    rows.push(...toRows(sectionItems(input, block, resolved, `m-${id}`, 'subtotal'), `m-${id}`, heading, true));
  });
  if (multi && totalStep) {
    const items: ChainItemVm[] = blocks.map((block, i) => {
      const yearly = block[block.length - 1];
      return card({
        id: `sum-${i}`,
        type: 'subtotal',
        label: yearly.label,
        description: '',
        display: formatStepValue(yearly.value, yearly.unit),
        operator: i === 0 ? null : '+',
      });
    });
    items.push(
      card({
        id: 'sum-total',
        type: 'subtotal',
        label: totalStep.label,
        description: totalStep.description,
        display: formatStepValue(totalStep.value, totalStep.unit),
        operator: '=',
      }),
    );
    rows.push({ id: 'sum', heading: null, items });
  }
  // The rate row is its own row, never packed onto the hours above it.
  rows.push(...toRows(sectionItems(input, steps.slice(tailAt), resolved, 'tail', 'result', undefined, false), 'tail', null, false));
  return rows;
}

/** The main chain's rows: segment rows per record level, then the level sum when both are on. */
export function buildChain(input: ChainInput): ChainRowVm[] {
  const { uc, result, levels } = input;
  const resolved = resolveUseCaseAnswers(uc, input.answers, [...levels], input.dealAcv, result.methods);
  const multi = result.sections.length > 1;
  const rows: ChainRowVm[] = [];
  result.sections.forEach((section, si) => {
    const level = section.level;
    const prefix = level ?? `s${si}`;
    const heading = multi && level ? LEVEL_LABEL[level] : null;
    let steps: readonly FormulaStep[] = section.steps;
    let lead: ChainRowVm[] = [];
    if (uc.id === 'uc-inbound-speed-to-lead' && section.columns) {
      lead = inboundRows(section, resolved);
      steps = section.steps.slice(3);
    }
    if (uc.id === 'uc-gtm-automation') {
      rows.push(...gtmRows(input, section, resolved));
      return;
    }
    const label = multi && level ? `${LEVEL_LABEL[level]} revenue` : undefined;
    const items = sectionItems(input, steps, resolved, prefix, multi ? 'subtotal' : 'result', label, lead.length === 0);
    const tail = toRows(items, prefix, lead.length ? null : heading, PACKED_ROWS.has(`${uc.id}/${level ?? ''}`));
    const first = lead[0];
    if (first && heading) first.heading = heading;
    rows.push(...lead, ...tail);
  });
  const gross = result.value;
  if (multi) {
    const items: ChainItemVm[] = result.sections.map((s, i) =>
      card({
        id: `sum-${i}`,
        type: 'subtotal',
        label: `${s.level ? LEVEL_LABEL[s.level] : ''} revenue`.trim(),
        description: '',
        display: moneyBig(s.value),
        operator: i === 0 ? null : '+',
      }),
    );
    items.push(
      card({ id: 'sum-total', type: 'result', label: 'Revenue', description: 'Both record levels add', display: moneyBig(gross), operator: '=' }),
    );
    rows.push({ id: 'sum', heading: null, items });
  }
  return rows;
}

/**
 * The "Realization and attribution" section: the main chain's lagging result repeated as the start
 * node, then `x Realization = Program value` and (next row) `x Attribution = Clay-attributed`.
 */
export function buildRealizationRows(mainRows: readonly ChainRowVm[], input: ChainInput): ChainRowVm[] {
  const { uc, result } = input;
  const last = mainRows.at(-1)?.items.at(-1);
  const f = factorSummary(result.value, input.realizationPct, input.attributionPct);
  const start =
    last?.kind === 'card'
      ? card({ id: 'ra-start', type: 'result', label: last.label, description: last.description, display: last.display })
      : card({ id: 'ra-start', type: 'result', label: 'Revenue', description: '', display: moneyBig(result.value) });
  return [
    {
      id: 'realization',
      heading: null,
      items: [
        start,
        card({ id: 'rz', type: 'factor', label: 'Realization', description: uc.realization.rationale, display: `${input.realizationPct}%`, operator: '×', factor: 'realization' }),
        card({ id: 'rz-out', type: 'subtotal', label: 'Program value', description: `${start.label} × realization`, display: f.programText, operator: '=' }),
      ],
    },
    {
      id: 'attribution',
      heading: null,
      items: [
        card({ id: 'at', type: 'factor', label: 'Attribution', description: uc.attribution.rationale, display: `${input.attributionPct}%`, operator: '×', factor: 'attribution' }),
        card({ id: 'at-out', type: 'attributed', label: attributedLabelFor(uc.outcome), description: 'Program value × attribution', display: f.attributedText, operator: '=' }),
      ],
    },
  ];
}

/** Every question key editable from a card, in chain order (a key twice would be a bug). */
export function chainEditableKeys(rows: readonly ChainRowVm[]): string[] {
  return rows.flatMap((r) => r.items.flatMap((i) => (i.kind === 'card' ? i.keys : [])));
}
